import React, {
  FC, useEffect, useMemo, useRef, useState,
} from 'react';
import { Spin } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import { useGetSpecialistsQuery } from '../../services/specialists';
import { useGetAvailabilitiesBySpecialistQuery } from '../../services/availabilities';
import { getSpecialities } from '../../services/specialities';
import { Speciality } from '../../model/Speciality';
import type Specialist from '../../model/Specialist';
import { AppointmentData, AppointmentType } from '../../model';
import { useAppDispatch, useAppSelector } from '../../hooks/redux-hooks';
import useClinicianAvailability from '../../hooks/useClinicianAvailability';
import {
  selectBookingAppointmentData,
  selectBookingTimezone,
  updateBookingAppointmentData,
  updateBookingData,
} from '../../redux/slices/booking';
import { CLIENT_DETAILS, DOCTORS_ROUTE } from '../../constants/routes';
import { formatAppointmentWhen, isDirectClinicianLink } from '../../utils/booking-flow';
import {
  findNextOpening, formatLongDate, formatShortDate, formatSlotTime, getBookableDays, sortByNextAvailable,
} from '../../utils/clinician-availability';
import { DATE_WITH_DASH, MONTH } from '../../constants/time-formats';
import { parseAppointmentLengthMinutes } from '../../utils/expand-timeslots';
import { generateSpecialities, getSpecialistDisplayName } from '../../utils/specialist';
import analytics from '../../infra/analytics';
import {
  PAGE_VIEW_EVENT, SELECT_SPECIALIST, SELECT_SPECIALTY, SITE_NAME,
} from '../../constants/analytics';
import SpecialtyPicker from './SpecialtyPicker';
import { TELECARE_PHONE } from '../../constants/contact';
import TimePicker, { MONTH_KEY } from './TimePicker';
import { DateStrip, MobileSection, SegmentedSlots } from './Mobile';
import FooterNavigator from '../../components/FooterNavigator';
import TimezoneSelect from '../../components/TimezoneSelect';
import {
  ClinicianHero, ClinicianOption, ClinicianSkeleton, PickedRow,
} from './Clinicians';
import {
  StepError, StepLead, StepPage, StepSection,
} from '../../components/StepPanel';

// Clinicians shown before "Show all".
const CLINICIAN_PREVIEW = 5;

const LOAD_ERROR = `We could not load appointments. Please refresh the page, or call Telecare on ${TELECARE_PHONE}.`;

// The step remounts for each specialty (see Appointment below), so the list is kept for the
// session: choosing a specialty must not blank and reload the specialty cards. A failed load
// is not kept, so the next visit tries again.
let cachedSpecialities: Speciality[] | undefined;

const useSpecialities = () => {
  const [specialities, setSpecialities] = useState<Speciality[] | undefined>(cachedSpecialities);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (cachedSpecialities) return;
    getSpecialities().then(({ specialities: list, error }) => {
      if (!error && list) cachedSpecialities = list;
      setHasError(Boolean(error));
      setSpecialities(list ?? []);
    });
  }, []);

  return { specialities, hasError };
};

// The numbered sections of the appointment step on phones.
type SectionKey = 'specialty' | 'clinician' | 'date' | 'time';

type Props = {
  specialtyId: string | null;
  directId: string | null;
};

const AppointmentStep: FC<Props> = ({ specialtyId, directId }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { search } = location;
  const appointment = useAppSelector(selectBookingAppointmentData) as Partial<AppointmentData>;
  const timezone = useAppSelector(selectBookingTimezone);
  const [showError, setShowError] = useState(false);
  const [opened, setOpened] = useState<SectionKey | null>(null);
  const [showAllClinicians, setShowAllClinicians] = useState(false);

  const { specialities, hasError: specialitiesFailed } = useSpecialities();
  const speciality = specialities?.find(({ id }) => id === specialtyId);
  const {
    data: specialists, isFetching: isLoadingSpecialists, isError: specialistsFailed,
  } = useGetSpecialistsQuery(specialtyId ?? '', { skip: !specialtyId });

  const directClinician = directId ? specialists?.find(({ id }) => id === directId) : undefined;
  const listed = useMemo(
    () => (directId ? specialists?.filter(({ id }) => id === directId) : specialists),
    [directId, specialists],
  );
  const {
    getAvailability, isAnyLoading, hasAnyError, now,
  } = useClinicianAvailability(listed, timezone);

  const clinician = listed?.find(({ id }) => id === appointment.specialistId);
  const availability = clinician ? getAvailability(clinician.id) : undefined;
  // Bookable days in the coming three months: drive "next available" and whether a calendar is shown.
  const days = availability?.isLoading ? undefined : availability?.days;
  const date = clinician ? appointment.date : undefined;
  const startTime = clinician ? appointment.startTime : undefined;

  // The calendar can page past the three-month window; each month is loaded when shown.
  // From the stored choice even before the clinician list has loaded (e.g. coming back via Edit).
  const [calendarMonth, setCalendarMonth] = useState(() => (appointment.date ?? now.clone().tz(timezone).format(DATE_WITH_DASH)).slice(0, 7));
  const { data: monthData, isFetching: isLoadingMonth } = useGetAvailabilitiesBySpecialistQuery({
    specialistId: clinician?.id ?? '',
    month: moment(calendarMonth, MONTH_KEY).format(MONTH),
    appointmentType: AppointmentType.NEW,
    timezone,
  }, { skip: !clinician });
  const monthDays = useMemo(
    () => (clinician && monthData && !isLoadingMonth ? getBookableDays(monthData, clinician.appointmentLength, now, timezone) : undefined),
    [clinician, monthData, isLoadingMonth, now, timezone],
  );

  const specialtyName = speciality?.name
    || generateSpecialities((directClinician ?? listed?.[0])?.specialities)
    || 'this specialty';

  const selectClinician = (specialist?: Specialist) => {
    setOpened(null);
    dispatch(updateBookingData({ specialist }));
    dispatch(updateBookingAppointmentData({
      specialistId: specialist?.id,
      // Set with the clinician, so the booking's end time always matches them.
      aptLength: specialist ? String(parseAppointmentLengthMinutes(specialist.appointmentLength)) : undefined,
      date: undefined,
      startTime: undefined,
    }));
  };
  const selectDate = (value?: string) => {
    setOpened(null);
    dispatch(updateBookingAppointmentData({ date: value, startTime: undefined }));
  };
  const selectTime = (value?: string) => {
    setShowError(false);
    dispatch(updateBookingAppointmentData({ startTime: value }));
  };

  // After choosing a specialty, bring its clinicians into view rather than the top of the page.
  // Runs after BookingHome's scroll-to-top, which fires on the route change.
  useEffect(() => {
    if ((location.state as { focus?: string } | null)?.focus !== 'clinicians') return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById('clinician-heading')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    analytics.logPageViewEvent(PAGE_VIEW_EVENT, {
      site: SITE_NAME,
      page_name: specialtyId ? SELECT_SPECIALIST : SELECT_SPECIALTY,
    });
  }, [specialtyId]);

  // A clinician's link books that clinician, whatever was chosen before.
  useEffect(() => {
    if (directClinician && appointment.specialistId !== directClinician.id) {
      selectClinician(directClinician);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directClinician, appointment.specialistId]);

  // Once a clinician's times are known, open the calendar on their soonest month; on a
  // clinician link also select the soonest day so times show straight away.
  const openedFor = useRef<string>();
  useEffect(() => {
    if (!clinician || !days?.length || date) return;
    // Only once per clinician, so paging the calendar is not undone when more data arrives.
    if (openedFor.current !== clinician.id || directId) {
      openedFor.current = clinician.id;
      setCalendarMonth(days[0].date.slice(0, 7));
    }
    if (directId) selectDate(days[0].date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinician?.id, days, directId, date]);

  // A new clock can move the chosen day into another month (1 Jan 00:30 Sydney is 31 Dec in
  // Perth), so show the month the converted day is in.
  useEffect(() => {
    if (date) setCalendarMonth(date.slice(0, 7));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timezone]);

  // Keep the patient's choice while it is still bookable.
  useEffect(() => {
    if (!date || !monthDays || date.slice(0, 7) !== calendarMonth) return;
    const day = monthDays.find((d) => d.date === date);
    if (!day) {
      selectDate(undefined);
    } else if (startTime && !day.slots.some(({ start }) => start === startTime)) {
      selectTime(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthDays, calendarMonth, date, startTime]);

  // A clinician link with no times: find the next real opening with someone else in the specialty.
  const directHasNoTimes = Boolean(directClinician && days && !days.length);
  const others = useMemo(
    () => (directHasNoTimes ? specialists?.filter(({ id }) => id !== directId) : undefined),
    [directHasNoTimes, specialists, directId],
  );
  const otherAvailability = useClinicianAvailability(others, timezone);
  const nextOpening = others && !otherAvailability.isAnyLoading
    ? findNextOpening(others, Object.fromEntries(others.map(({ id }) => [id, otherAvailability.getAvailability(id).days])))
    : undefined;

  // Moving to the clinician list starts a fresh choice.
  const goToSpecialty = (id: string) => {
    selectClinician(undefined);
    navigate(`${DOCTORS_ROUTE}?specialty=${encodeURIComponent(id)}`, { state: { focus: 'clinicians' } });
  };

  const handleSelectSpecialty = (selected: Speciality) => {
    if (selected.id === specialtyId) {
      setOpened(null);
      return;
    }
    dispatch(updateBookingData({ speciality: selected }));
    goToSpecialty(selected.id);
  };

  const handleContinue = () => {
    if (!clinician || !date || !startTime) {
      setShowError(true);
      return;
    }
    if (speciality) {
      dispatch(updateBookingData({ speciality }));
    }
    navigate(`${CLIENT_DETAILS}${search}`);
  };

  const othersWithTimes = (listed ?? []).filter(({ id }) => id !== clinician?.id && getAvailability(id).days.length);
  // Only say nobody is bookable when every clinician's times actually loaded.
  const nobodyBookable = !directId && !!listed?.length && !isAnyLoading && !hasAnyError && !othersWithTimes.length && !days?.length;

  const renderNoTimes = () => {
    if (!clinician || !days || days.length) return null;
    const name = getSpecialistDisplayName(clinician);

    if (availability?.hasError) {
      return (
        <div className="empty">
          <b>{`We could not load ${name}'s times.`}</b>
          {`Please refresh the page, or call Telecare on ${TELECARE_PHONE}.`}
        </div>
      );
    }

    if (directId) {
      if (!nextOpening) {
        let detail = `Nobody in ${specialtyName} is bookable at the moment. Please check back soon, or call Telecare on ${TELECARE_PHONE}.`;
        if (others && otherAvailability.isAnyLoading) {
          detail = `Checking other ${specialtyName} clinicians…`;
        } else if (otherAvailability.hasAnyError) {
          detail = `We could not check other ${specialtyName} clinicians. Please refresh the page, or call Telecare on ${TELECARE_PHONE}.`;
        }
        return (
          <div className="empty">
            <b>{`${name} has no available appointments right now.`}</b>
            {detail}
          </div>
        );
      }
      return (
        <div className="empty">
          <b>{`${name} has no available appointments right now.`}</b>
          {`The next opening in ${specialtyName} is with ${getSpecialistDisplayName(nextOpening.specialist)} on ${formatShortDate(nextOpening.date)}.`}
          <div style={{ marginTop: 12 }}>
            <button type="button" className="btn btn-g" onClick={() => specialtyId && goToSpecialty(specialtyId)}>
              {`See other ${specialtyName} clinicians`}
            </button>
          </div>
        </div>
      );
    }

    if (nobodyBookable) return null;
    const count = othersWithTimes.length;
    return (
      <div className="empty">
        <b>{`${name} has no available appointments right now.`}</b>
        {isAnyLoading
          ? `Checking other ${specialtyName} clinicians…`
          : `There ${count === 1 ? 'is' : 'are'} ${count} other ${specialtyName} clinician${count === 1 ? '' : 's'} with availability.`}
        <div style={{ marginTop: 12 }}>
          <button type="button" className="btn btn-g" onClick={() => selectClinician(undefined)}>
            {`See other ${specialtyName} clinicians`}
          </button>
        </div>
      </div>
    );
  };

  const loadFailed = specialitiesFailed || specialistsFailed;
  const noTimes = Boolean(clinician && days && !days.length);
  const isComplete = Boolean(clinician && date && startTime);
  const selectedDay = monthDays?.find((d) => d.date === date);

  // Phones: which numbered section is open — the first one still to choose, unless the
  // patient taps "Change" on an earlier one.
  let firstIncomplete: SectionKey = 'time';
  if (!specialtyId) firstIncomplete = 'specialty';
  else if (!clinician || noTimes) firstIncomplete = 'clinician';
  else if (!date) firstIncomplete = 'date';
  const openSection = opened ?? firstIncomplete;

  const getHint = () => {
    if (isComplete) return `${getSpecialistDisplayName(clinician as Specialist)} · ${formatAppointmentWhen(appointment)}`;
    if (date) return 'Choose a time to continue';
    if (clinician) return 'Choose a date and time to continue';
    return 'Choose a clinician, date and time to continue';
  };

  const specialtyPicker = specialities
    ? <SpecialtyPicker specialities={specialities} selectedId={specialtyId} onSelect={handleSelectSpecialty} />
    : <Spin />;

  // Soonest available first, once every clinician's times are known (so the list does not
  // reshuffle while loading); long lists show the first few with "Show all".
  const orderedClinicians = useMemo(() => (listed && !isAnyLoading
    ? sortByNextAvailable(listed, (id) => getAvailability(id).days[0]?.date ?? null)
    : listed), [listed, isAnyLoading, getAvailability]);
  const shownClinicians = useMemo(() => {
    if (!orderedClinicians || showAllClinicians || orderedClinicians.length <= CLINICIAN_PREVIEW) return orderedClinicians;
    const first = orderedClinicians.slice(0, CLINICIAN_PREVIEW);
    // Never hide the clinician already chosen.
    return clinician && !first.some(({ id }) => id === clinician.id) ? [...first, clinician] : first;
  }, [orderedClinicians, showAllClinicians, clinician]);
  const hiddenCount = (orderedClinicians?.length ?? 0) - (shownClinicians?.length ?? 0);

  // Larger screens: a chosen specialty or clinician collapses to one line with "Change".
  const specialtyCollapsed = Boolean(specialtyId && speciality && opened !== 'specialty');
  const clinicianCollapsed = Boolean(!directId && clinician && !noTimes && opened !== 'clinician');

  const clinicianContent = (
    <>
      {isLoadingSpecialists && <ClinicianSkeleton />}
      {!isLoadingSpecialists && directId && (directClinician ? (
        <>
          <ClinicianHero specialist={directClinician} specialtyName={specialtyName} availability={getAvailability(directClinician.id)} />
          <p className="switchline">
            {'Looking for someone else? '}
            <button type="button" onClick={() => specialtyId && goToSpecialty(specialtyId)}>
              {`See all ${specialtyName} clinicians`}
            </button>
          </p>
        </>
      ) : (
        <div className="empty">
          <b>We could not find this clinician.</b>
          <button type="button" className="btn btn-g" onClick={() => specialtyId && goToSpecialty(specialtyId)}>{`See all ${specialtyName} clinicians`}</button>
        </div>
      ))}
      {!isLoadingSpecialists && !directId && (
        <div className="cliniclist">
          {shownClinicians?.map((specialist) => (
            <ClinicianOption
              key={specialist.id}
              specialist={specialist}
              specialtyName={specialtyName}
              availability={getAvailability(specialist.id)}
              selected={specialist.id === clinician?.id}
              onSelect={() => (specialist.id !== clinician?.id ? selectClinician(specialist) : setOpened(null))}
            />
          ))}
          {listed?.length === 0 && <div className="empty">There are no clinicians in this specialty at the moment.</div>}
          {hiddenCount > 0 && (
            <button type="button" className="btn btn-g show-all" onClick={() => setShowAllClinicians(true)}>
              {`Show all ${orderedClinicians?.length} ${specialtyName} clinicians`}
            </button>
          )}
        </div>
      )}
      {nobodyBookable && (
        <div className="empty">
          <b>{`Nobody in ${specialtyName} is bookable at the moment.`}</b>
          {`Please check back soon, or call Telecare on ${TELECARE_PHONE}.`}
        </div>
      )}
      {renderNoTimes()}
    </>
  );

  return (
    <StepPage>
      <div className="d-only">
        <h2>{directId ? 'Book your appointment' : 'Find your appointment'}</h2>
        <StepLead>
          {directId
            ? 'Pick a time that suits you. The fee is on the right — nothing personal is asked for until you’ve chosen.'
            : 'Choose a specialty, then a clinician and a time. You can see the fee before you enter any personal details.'}
        </StepLead>
      </div>

      {loadFailed && <div className="notice n-err" role="alert">{LOAD_ERROR}</div>}

      {/* Larger screens: the mockup's single page. */}
      <div className="d-only">
        {!directId && (
          <StepSection aria-labelledby="specialty-heading">
            <h3 id="specialty-heading">1 · Specialty</h3>
            {specialtyCollapsed ? (
              <PickedRow value={speciality?.name} onChange={() => setOpened('specialty')} />
            ) : specialtyPicker}
          </StepSection>
        )}

        {specialtyId && (
          <StepSection aria-labelledby="clinician-heading">
            {!directId && <h3 id="clinician-heading">2 · Clinician</h3>}
            {clinicianCollapsed && clinician ? (
              <PickedRow
                value={getSpecialistDisplayName(clinician)}
                detail={days?.[0] ? `Next available ${formatShortDate(days[0].date)}` : null}
                onChange={() => setOpened('clinician')}
              />
            ) : clinicianContent}
          </StepSection>
        )}

        {clinician && availability?.isLoading && <StepSection><Spin /></StepSection>}
        {clinician && !!days?.length && (
          <StepSection aria-labelledby="when-heading">
            <h3 id="when-heading">{directId ? 'Choose a time' : '3 · Date & time'}</h3>
            <TimePicker
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
              days={monthDays ?? []}
              isLoading={!monthDays}
              date={date}
              startTime={startTime}
              onSelectDate={selectDate}
              onSelectTime={selectTime}
              timezone={timezone}
              header={<TimezoneSelect at={date} />}
            />
          </StepSection>
        )}

        {showError && <StepError role="alert">Please choose a clinician, a date and a time to continue.</StepError>}
      </div>

      {/* Phones: the ADHD portal's numbered sections. */}
      <div className="m-only">
        {!directId && (
          <MobileSection
            index={1}
            label="Specialty"
            value={speciality?.name}
            placeholder="Select a specialty"
            open={openSection === 'specialty'}
            done={!!specialtyId}
            onOpen={() => setOpened('specialty')}
          >
            {specialtyPicker}
          </MobileSection>
        )}
        {specialtyId && (
          <MobileSection
            index={directId ? 1 : 2}
            label="Clinician"
            value={clinician && getSpecialistDisplayName(clinician)}
            placeholder="Select a clinician"
            detail={clinician && days?.[0] ? `Next available ${formatShortDate(days[0].date)}` : null}
            open={openSection === 'clinician'}
            done={!!clinician && !noTimes}
            onOpen={() => setOpened('clinician')}
          >
            {clinicianContent}
          </MobileSection>
        )}
        {clinician && availability?.isLoading && <Spin />}
        {clinician && !!days?.length && (
          <MobileSection
            index={directId ? 2 : 3}
            label="Date"
            value={date && formatLongDate(date)}
            placeholder="Select a date"
            open={openSection === 'date'}
            done={!!date}
            onOpen={() => setOpened('date')}
          >
            <DateStrip
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
              days={monthDays ?? []}
              isLoading={!monthDays}
              date={date}
              onSelectDate={selectDate}
              timezone={timezone}
            />
          </MobileSection>
        )}
        {clinician && date && (
          <MobileSection
            index={directId ? 3 : 4}
            label="Time"
            value={startTime && `${formatSlotTime(startTime)} ${moment.tz(date, timezone).format('z')}`}
            placeholder="Select a time"
            open={openSection === 'time'}
            done={!!startTime}
            onOpen={() => setOpened('time')}
          >
            <TimezoneSelect at={date} />
            {selectedDay ? <SegmentedSlots key={selectedDay.date} day={selectedDay} startTime={startTime} onSelectTime={selectTime} /> : <Spin />}
          </MobileSection>
        )}
      </div>

      {/* Nothing to continue with while the chosen clinician has no times. */}
      {!noTimes && (
        <FooterNavigator onNext={handleContinue} disabled={!isComplete} hint={getHint()} />
      )}
    </StepPage>
  );
};

// Remount when the specialty or clinician in the address changes, so nothing carries over between them.
const Appointment: FC = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const specialtyId = params.get('specialty');
  const directId = isDirectClinicianLink(search) ? params.get('specialist') : null;

  return <AppointmentStep key={`${specialtyId}|${directId}`} specialtyId={specialtyId} directId={directId} />;
};

export default Appointment;
